import React, { useEffect, useRef, useState } from 'react'
import CSVFileValidator from 'csv-file-validator'
import { FileUpload, FileSpreadsheet, X } from 'tabler-icons-react'

import { useAppDispatch, useAppSelector } from '../../hooks/redux-hooks'

import ExperianCSVConfig from '../../config/experian-csv.config'
import DigiCSVConfig from '../../config/digi-csv.config'
import CdCSVConfig from '../../config/cd-csv.config'

import Header from '../../components/Header/Header'

import { generateCustomerComboHistory } from '../../store/services/History.services'

import { chunk, successAlert } from '../../utils'

import styles from './ComboHistory.module.css'
import FileUploadHistoryRules from '../../components/UploadScore/FileUploadHistoryRules/FileUploadHistoryRules'

type ProgressState = {
  icon: string
  title: string
  content: string
  status: string
  process: number
  error: string | null
  data?: { id: string; score: number }[] | null
  csvHeader?: string[]
}

type progressDataType = {
  fileVerification: ProgressState
  dataVerification: ProgressState
  uploadProcess: ProgressState
}

const progressInitialData = {
  fileVerification: {
    icon: 'file-check',
    title: 'Verify file',
    content: 'waiting csv file...',
    status: 'idle',
    process: 0,
    error: null
  },
  dataVerification: {
    icon: 'data-check',
    title: 'Validate csv records',
    content: 'waiting for csv data...',
    status: 'idle',
    process: 0,
    error: null,
    data: null,
    header: []
  },
  uploadProcess: {
    icon: 'cloud-upload',
    title: 'Upload to cloud',
    content: 'waiting for process....',
    status: 'idle',
    process: 0,
    error: null
  }
}

function ComboHistory() {
  const dispatch = useAppDispatch()

  const upload_score = useAppSelector((state) => state.uploadScore)

  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const [uploadFile, setUploadFile] = useState<File | null>(null)

  const [uploadStarted, setUploadStarted] = useState<boolean>(false)

  const [scoreType, setScoreType] = useState<string>('')

  const [progressData, setProgressData] =
    useState<progressDataType>(progressInitialData)

  const fileUploadIconClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (e: any) => {
    if (e.target.files !== null && e.target.files.length > 0) {
      setUploadFile(e.target.files[0])
      e.target.value = ''
    }
  }

  const clearFileInput = () => {
    setUploadFile(null)
    setUploadStarted(false)
    setProgressData(progressInitialData)
    setScoreType('')
  }

  // todo : set up config for csv validation
  const getCSVConfig = (scoreType: string) => {
    if (scoreType === 'DIGI' || scoreType === 'CELCOM') {
      return DigiCSVConfig
    } else if (scoreType === 'CD') {
      return CdCSVConfig
    } else {
      return ExperianCSVConfig
    }
  }

  // File format and extensions checking
  useEffect(() => {
    if (uploadStarted === true) {
      let fileChecking = true

      setProgressData({
        ...progressData,
        fileVerification: {
          ...progressData.fileVerification,
          status: 'pending',
          process: 10,
          content: 'verifying file...'
        }
      })

      if (uploadFile == null) {
        fileChecking = false

        setProgressData({
          ...progressData,
          fileVerification: {
            ...progressData.fileVerification,
            status: 'failed',
            process: 20,
            content: 'Could not find upload file.',
            error: 'Could not find upload file.'
          }
        })
      }

      if (uploadFile?.type !== 'text/csv' && fileChecking === true) {
        fileChecking = false
        setProgressData({
          ...progressData,
          fileVerification: {
            ...progressData.fileVerification,
            status: 'failed',
            process: 40,
            content: 'Uploaded file is not valid.',
            error: ''
          }
        })
      }

      if (fileChecking === true) {
        setProgressData({
          ...progressData,
          fileVerification: {
            ...progressData.fileVerification,
            status: 'completed',
            process: 100,
            content: 'File has been verified successfully.'
          }
        })
      }

      if (progressData.fileVerification.status === 'completed') {
      }
    }
  }, [uploadStarted])

  // verify score data

  useEffect(() => {
    if (progressData.fileVerification.status === 'completed') {
      setProgressData({
        ...progressData,
        dataVerification: {
          ...progressData.dataVerification,
          status: 'pending',
          process: 10,
          content: 'verifying the score data...'
        }
      })

      CSVFileValidator(uploadFile ?? '', getCSVConfig(scoreType))
        .then((result) => {
          const csv_data = result.data

          setProgressData({
            ...progressData,
            dataVerification: {
              ...progressData.dataVerification,
              status: 'completed',
              process: 100,
              csvHeader: ['NRIC / MSISDN', 'Channel', 'Order Date'],
              data: csv_data,
              content: result.data.length + ' - rows have been verified'
            }
          })
        })
        .catch((err) => {
          setProgressData({
            ...progressData,
            dataVerification: {
              ...progressData.dataVerification,
              status: 'failed',
              process: 60,
              content: err.response.message
            }
          })
        })
    }
  }, [progressData.fileVerification.status])

  // csv file upload to s3 drive
  useEffect(() => {
    if (progressData.dataVerification.status === 'completed') {
      setProgressData({
        ...progressData,
        uploadProcess: {
          ...progressData.uploadProcess,
          status: 'pending',
          process: 10,
          content: 'uploading the csv file...'
        }
      })
      console.log('progress on uploading to s3')

      if (
        progressData.dataVerification.data &&
        progressData.dataVerification.data?.length > 0
      ) {
        const csv_upload_limit: number = Number(
          process.env.REACT_APP_CSV_UPLOAD_LIMIT
        )

        setProgressData({
          ...progressData,
          uploadProcess: {
            ...progressData.uploadProcess,
            process: 30
          }
        })

        const chunkData = chunk(
          progressData.dataVerification.data,
          csv_upload_limit
        )

        setProgressData({
          ...progressData,
          uploadProcess: {
            ...progressData.uploadProcess,
            process: 50
          }
        })

        if (chunkData.length > 0) {
          const progressLimit = 40 / chunkData.length

          let progressInit = 50

          chunkData.forEach((chunkAry: any) => {
            progressInit = progressInit + progressLimit

            console.log({ chunkAry })
            dispatch(
              generateCustomerComboHistory({
                rows: chunkAry.length,
                csvdata: chunkAry
              })
            )

            setProgressData({
              ...progressData,
              uploadProcess: {
                ...progressData.uploadProcess,
                process: progressInit
              }
            })
          })

          setProgressData({
            ...progressData,
            uploadProcess: {
              ...progressData.uploadProcess,
              process: progressInit,
              status: 'upload_requested'
            }
          })
        }
      }
    }
  }, [progressData.dataVerification.status])

  useEffect(() => {
    if (
      upload_score.loading === 'completed' &&
      progressData.uploadProcess.status === 'upload_requested'
    ) {
      setProgressData({
        ...progressData,
        uploadProcess: {
          ...progressData.uploadProcess,
          status: 'completed',
          process: 100,
          content: 'All files have been uploaded '
        }
      })

      successAlert('File has been successfully uploaded', () => {
        clearFileInput()
      })
    }
  }, [upload_score])

  let fileInputContent = (
    <React.Fragment>
      <FileUpload
        onClick={() => fileUploadIconClick()}
        size={112}
        color="#f5f5f5"
      />
      <span>Click icon to upload .csv file</span>
    </React.Fragment>
  )

  if (uploadFile !== null) {
    fileInputContent = (
      <React.Fragment>
        <div className={styles.uploadedInputFile}>
          <X
            className={styles.clearUploadFile}
            size={20}
            color="red"
            onClick={() => clearFileInput()}
          />
          <FileSpreadsheet
            size={112}
            color="#f5f5f5"
            className={styles.uploadedIcon}
          />
        </div>
        <span className={styles.uploadedName}>{uploadFile.name}</span>
      </React.Fragment>
    )
  }

  return (
    <div className="wrapper">
      <Header />
      <div className={styles.page_container}>
        <div className={styles.container}>
          <div className={styles.upload_box_container}>
            <div className={styles.upload_book_left}>
              <h2>Upload Combo History</h2>
              <div className={styles.upload_box}>
                {fileInputContent}
                {uploadFile === null ? <FileUploadHistoryRules /> : null}
                <button
                  disabled={uploadFile ? false : true}
                  className={styles.upload_button}
                  type="button"
                  onClick={() => setUploadStarted(true)}
                >
                  {uploadStarted ? <div className="dot-pulse"></div> : 'Upload'}
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".csv"
                  id="uploadInput"
                  onChange={(e) => handleFileChange(e)}
                  className={styles.upload_input}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ComboHistory
